/**
 * The glossary - issue #75.
 *
 * The terms and acronyms the model uses, so the same abbreviation means the same
 * thing in every requirement. Two sets are merged by the server: the base set
 * every program shares, and the program's own, where a program entry replaces a
 * base one with the same term.
 *
 * Read-only on purpose. The entries live in CSV files and change through a merge
 * request like any other part of the model; this screen only shows them, and
 * says where the files are.
 *
 * Search and the Base / Program filter are held in the URL, so a search is a
 * link. Each term has an anchor id, so an entry is a link too.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { Link as RouterLink, useLocation, useSearchParams } from 'react-router-dom'
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { ApiError, api } from '../api'
import type { GlossaryEntry, GlossaryPage } from '../schema'
import { MONOSPACE } from '../theme'
import EmptyState from '../components/common/EmptyState'
import { readSearch, writeSearch } from '../components/entity/rowFilter'

/** The query parameter holding the scope filter. Absent means all. */
const SCOPE_KEY = 'scope'

type Scope = 'all' | 'base' | 'program'

/** Where the entries are edited, said on the page in more than one place. */
const BASE_FILE = 'libraries/echelon/glossary.csv'
const PROGRAM_FILE = 'glossary.csv'

/** What the section with no group is called. */
const NO_GROUP = 'Other'

/**
 * The anchor id for a term.
 *
 * Lower-cased, every run of characters that is not a letter or digit becomes one
 * hyphen, none at either end, prefixed `term-`: "TBR" is `term-tbr` and
 * "I&T" is `term-i-t`. Derived from the term alone, so the link to an entry
 * survives the glossary being re-sorted, regrouped or filtered.
 */
function termAnchor(term: string): string {
  const slug = term
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, '-')
    .replace(/^-+|-+$/g, '')
  return `term-${slug || 'entry'}`
}

function readScope(params: URLSearchParams): Scope {
  const value = params.get(SCOPE_KEY)
  return value === 'base' || value === 'program' ? value : 'all'
}

/** Whether the entry's term, expansion or meaning contains the (lower-cased) query. */
function matches(entry: GlossaryEntry, needle: string): boolean {
  if (!needle) return true
  return [entry.term, entry.stands_for, entry.meaning].some((text) =>
    text.toLowerCase().includes(needle),
  )
}

/**
 * The entries under their section headings, in the order the server gave.
 *
 * `groups` decides the order of the sections; entries keep the order they came
 * in within one. A group an entry names that `groups` does not list is shown
 * after the listed ones rather than dropped, and the no-group section is last.
 */
function sections(page: GlossaryPage, entries: GlossaryEntry[]): { name: string; entries: GlossaryEntry[] }[] {
  const order = [...page.groups.filter((name) => name !== '')]
  for (const entry of page.entries) {
    if (entry.group && !order.includes(entry.group)) order.push(entry.group)
  }
  order.push('')
  return order
    .map((name) => ({ name, entries: entries.filter((entry) => entry.group === name) }))
    .filter((section) => section.entries.length > 0)
}

export default function Glossary() {
  const [page, setPage] = useState<GlossaryPage | null>(null)
  const [error, setError] = useState<ApiError | null>(null)
  const [params, setParams] = useSearchParams()
  const { hash, search: locationSearch } = useLocation()

  useEffect(() => {
    api
      .glossary()
      .then(setPage)
      .catch((failure: ApiError) => setError(failure))
  }, [])

  const query = readSearch(params)
  const scope = readScope(params)
  const needle = query.trim().toLowerCase()

  const shown = useMemo(
    () =>
      (page?.entries ?? []).filter(
        (entry) => (scope === 'all' || entry.scope === scope) && matches(entry, needle),
      ),
    [page, scope, needle],
  )
  const grouped = useMemo(() => (page ? sections(page, shown) : []), [page, shown])

  // Arriving with a hash scrolls that entry into view. The browser's own jump
  // runs at navigation, before the fetch has finished and the row exists, so it
  // misses; this runs once the rows are rendered. Once per hash, so it does not
  // drag the page back when the reader then searches.
  const scrolledTo = useRef('')
  useEffect(() => {
    if (!hash || scrolledTo.current === hash) return
    const target = document.getElementById(hash.slice(1))
    if (!target) return
    target.scrollIntoView()
    scrolledTo.current = hash
  }, [hash, grouped])

  if (error) return <Alert severity="error">{error.message}</Alert>
  if (!page) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  const programLabel = page.program || 'Program'
  const filtered = Boolean(needle) || scope !== 'all'

  const changeSearch = (next: string) => setParams(writeSearch(params, next), { replace: true })
  const changeScope = (next: Scope) =>
    setParams(
      (now) => {
        const updated = new URLSearchParams(now)
        if (next === 'all') updated.delete(SCOPE_KEY)
        else updated.set(SCOPE_KEY, next)
        return updated
      },
      { replace: true },
    )

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h5">Glossary</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '90ch' }}>
          The terms and acronyms used in BEST{page.program ? ` and in ${page.program}` : ' and in this program'}.
          Where a term has more than one common reading, this is the one meant here.
        </Typography>
      </Box>

      {page.problems.length > 0 && (
        <Accordion disableGutters variant="outlined" sx={{ borderColor: 'warning.main' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', color: 'warning.main' }}>
              <WarningAmberIcon fontSize="small" />
              <Typography variant="body2" sx={{ color: 'text.primary' }}>
                {page.problems.length === 1
                  ? '1 glossary line could not be read'
                  : `${page.problems.length} glossary lines could not be read`}
              </Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack divider={<Box sx={{ borderBottom: 1, borderColor: 'divider' }} />}>
              {page.problems.map((problem, index) => (
                <Stack
                  key={`${problem.source}:${problem.line}:${index}`}
                  direction="row"
                  spacing={1.5}
                  sx={{ py: 0.5, alignItems: 'baseline', flexWrap: 'wrap' }}
                >
                  <Typography variant="caption" sx={{ fontFamily: MONOSPACE }}>
                    {problem.source}:{problem.line}
                  </Typography>
                  <Typography variant="body2">{problem.reason}</Typography>
                </Stack>
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}

      {page.entries.length === 0 ? (
        <EmptyState
          title="No glossary yet"
          description={
            `The glossary has not been imported for this program and branch yet. ` +
            `It comes in with the next import. The shared terms are read from ${BASE_FILE}, ` +
            `and this program's own from ${PROGRAM_FILE} in its model folder.`
          }
        />
      ) : (
        <>
          <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}>
            <TextField
              size="small"
              type="search"
              label="Search the glossary"
              value={query}
              onChange={(event) => changeSearch(event.target.value)}
              sx={{ minWidth: 280 }}
            />
            <ToggleButtonGroup
              size="small"
              exclusive
              value={scope}
              onChange={(_event, next: Scope | null) => next && changeScope(next)}
              aria-label="Filter by where a term is defined"
            >
              <ToggleButton value="all">All</ToggleButton>
              <ToggleButton value="base">Base</ToggleButton>
              <ToggleButton value="program">Program</ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="body2" color="text.secondary" role="status">
              {filtered
                ? `${shown.length} of ${page.entries.length} ${page.entries.length === 1 ? 'entry' : 'entries'}`
                : `${shown.length} ${shown.length === 1 ? 'entry' : 'entries'}`}
            </Typography>
          </Stack>

          {grouped.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No glossary entries match.
            </Typography>
          )}

          {grouped.map((section) => (
            <Paper
              key={section.name || '(none)'}
              sx={{ p: 1.5 }}
              component="section"
              aria-label={section.name || NO_GROUP}
            >
              <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline', mb: 0.5 }}>
                <Typography variant="subtitle1" component="h2">
                  {section.name || NO_GROUP}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {section.entries.length}
                </Typography>
              </Stack>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: '24%' }}>Term</TableCell>
                    <TableCell sx={{ width: '22%' }}>Stands for</TableCell>
                    <TableCell>Meaning</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {section.entries.map((entry) => {
                    const id = termAnchor(entry.term)
                    return (
                      <TableRow
                        key={`${entry.scope}:${entry.term}`}
                        id={id}
                        hover
                        selected={hash === `#${id}`}
                        sx={{ scrollMarginTop: 72 }}
                      >
                        <TableCell sx={{ verticalAlign: 'top' }}>
                          <RouterLink
                            to={{ search: locationSearch, hash: `#${id}` }}
                            style={{ fontFamily: MONOSPACE, fontWeight: 600, color: 'inherit' }}
                          >
                            {entry.term}
                          </RouterLink>
                          <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap', rowGap: 0.5 }}>
                            <Tooltip title={entry.source}>
                              <Chip
                                size="small"
                                variant="outlined"
                                color={entry.scope === 'program' ? 'primary' : 'default'}
                                label={entry.scope === 'program' ? programLabel : 'Base'}
                                sx={{ height: 20 }}
                              />
                            </Tooltip>
                            {entry.overrides_base && (
                              <Tooltip title="This program's definition replaces the base definition of the same term">
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  label="Replaces base"
                                  sx={{ height: 20, color: 'text.secondary' }}
                                />
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'top' }}>
                          {entry.stands_for || (
                            <Typography component="span" color="text.disabled">
                              —
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'top' }}>{entry.meaning}</TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </Paper>
          ))}

          <Typography variant="caption" color="text.secondary" sx={{ maxWidth: '90ch' }}>
            To change an entry, edit the file and open a merge request: the shared terms are in{' '}
            <code>{BASE_FILE}</code>, and this program&apos;s own are in <code>{PROGRAM_FILE}</code> in
            its model folder.
          </Typography>
        </>
      )}
    </Stack>
  )
}
